// frontend/src/modules/projects/components/BudgetPanel.tsx
import * as React from 'react';
import { VISUAL } from '../../../design-system/visual';
import { formatMoney } from '../../../design-system/format';
import type { BudgetVsActual } from '../types';

function Bar({ pct, tone }: { pct: number; tone: 'saffron' | 'success' }) {
  const clamped = Math.max(0, Math.min(pct, 100));
  const over = pct > 100;
  const fill = over
    ? 'bg-rose-500'
    : tone === 'saffron' ? 'bg-saffron' : 'bg-success';
  return (
    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
      <div className={[fill, 'h-full rounded-full transition-all'].join(' ')} style={{ width: `${clamped}%` }} />
    </div>
  );
}

export function BudgetPanel({ budget, currency }: { budget: BudgetVsActual; currency: string }) {
  const hasAmount = budget.budget_amount != null && budget.budget_amount > 0;
  const hasHours = budget.budget_hours != null && budget.budget_hours > 0;

  if (!hasAmount && !hasHours) {
    return (
      <section className={[VISUAL.bento, 'p-6'].join(' ')}>
        <p className={[VISUAL.eyebrow, 'mb-2'].join(' ')}>Budget</p>
        <p className="text-[13px] text-muted-foreground">
          No budget set. Edit the project to add a budget amount or hours, plus an hourly rate.
        </p>
        <dl className="mt-4 grid grid-cols-3 gap-4 text-[13px]">
          <Metric label="Tracked" value={`${budget.actual_hours}h`} />
          <Metric label="Billable" value={`${budget.billable_hours}h`} />
          <Metric label="Billable value" value={formatMoney(budget.billable_value, currency)} />
        </dl>
      </section>
    );
  }

  return (
    <section className={[VISUAL.bento, 'p-6 space-y-5'].join(' ')}>
      <p className={VISUAL.eyebrow}>Budget vs actual</p>

      {hasAmount && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[13px] font-semibold text-foreground">Amount</span>
            <span className="text-[12.5px] text-muted-foreground tabular-nums">
              {formatMoney(budget.billable_value, currency)} / {formatMoney(budget.budget_amount!, currency)}
              {budget.amount_pct != null && <span className="ml-1.5 font-medium">({budget.amount_pct}%)</span>}
            </span>
          </div>
          <Bar pct={budget.amount_pct ?? 0} tone="saffron" />
        </div>
      )}

      {hasHours && (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[13px] font-semibold text-foreground">Hours</span>
            <span className="text-[12.5px] text-muted-foreground tabular-nums">
              {budget.actual_hours}h / {budget.budget_hours}h
              {budget.hours_pct != null && <span className="ml-1.5 font-medium">({budget.hours_pct}%)</span>}
            </span>
          </div>
          <Bar pct={budget.hours_pct ?? 0} tone="success" />
        </div>
      )}

      <dl className="grid grid-cols-3 gap-4 text-[13px] pt-1">
        <Metric label="Tracked" value={`${budget.actual_hours}h`} />
        <Metric label="Billable" value={`${budget.billable_hours}h`} />
        <Metric label="Billable value" value={formatMoney(budget.billable_value, currency)} />
      </dl>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11.5px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-[14px] font-semibold text-foreground tabular-nums mt-0.5">{value}</dd>
    </div>
  );
}
